/**
 * @file PostItem.jsx
 * @description This component renders a news item card with user interactions such as like, comment, and share.
 * It also handles displaying comments and replies to comments.
 * @module NewsItem
 */
import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Heart,
	MessageCircle,
	Share2,
	MoreVertical,
	Trash2,
	AlertTriangle,
	Send,
	Star,
	Award,
	Trophy,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from "@/components/ui/dialog";
import CommentsSection from "./CommentsSection";
import { getYouTubeEmbedUrl } from "../../js/utils/mediaUtils";
import { useNavigate } from "react-router-dom";
import useNewsfeedStore from "../../zustand/UseNewsfeed";
import useUserStore from "../../zustand/UseUserStore";
import { showErrorToast } from "../../js/utils/errors";
import { IMAGE_PATH } from "../../api/apiConfig";
import AudioPlayer from "@/components/AudioPlayer";

const PostItem = ({
	item,
	lastItemRef,
	onPostSelected,
	isCompact = true,
	showComments = true,
	selectedCommentsPost,
	setSelectedCommentsPost,
	onSendLumens,
	currentContestFilter = 0,
}) => {
	const imagePath = IMAGE_PATH;

	// Helper function to get the correct ID based on post type
	const getPostId = (post) => {
		if (post.GratitudeID !== undefined && post.GratitudeID !== null) {
			return post.GratitudeID;
		} else if (post.PrideID !== undefined && post.PrideID !== null) {
			return post.PrideID;
		}
		return post.GlowID;
	};

	// Helper function to detect post type
	const detectPostType = (post) => {
		if (post.GratitudeID !== undefined && post.GratitudeID !== null) {
			return "grateful";
		} else if (post.PrideID !== undefined && post.PrideID !== null) {
			return "pride";
		} else if (post.Type === "grateful" || post.Type === "Grateful") {
			return "grateful";
		} else if (post.Type === "pride" || post.Type === "Pride") {
			return "pride";
		}
		return "glow";
	};

	const itemId = getPostId(item);
	const postType = detectPostType(item);
	const shareArrow = `${imagePath}/icon/shareArrow.svg`;

	const navigate = useNavigate();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [shareItemId, setShareItemId] = useState(-1);

	const newsfeed = useNewsfeedStore();
	const userStore = useUserStore();

	const isOwnPost = item.MemberID === userStore.user.MemberID;
	const likeIndex = newsfeed.likedItems.indexOf(itemId);
	const isCommenting = isCompact
		? newsfeed.isCommenting.includes(itemId)
		: true;

	// Handle post deletion with API call
	const handleDeletePost = () => {
		setIsDeleting(true);
		setTimeout(() => (document.body.style.pointerEvents = ""), 300);
		try {
			newsfeed.handleDelete(itemId);
		} catch (error) {
			console.error("Failed to delete post:", error);
		} finally {
			setIsDeleteDialogOpen(false);
			setIsDeleting(false);
		}
	};

	// Determine if this is a "glow" post (high lumens)
	// Only show glow badge for actual glow post types (not pride or grateful posts)
	const isGlowPost = postType === "glow" && item.Lumens > 0;
	console.log("Post debug:", {
		GlowID: item.GlowID,
		Lumens: item.Lumens,
		Score: item.Score,
		ContestID: item.ContestID,
		postType,
		isGlowPost,
		hasPoints: item.Score !== undefined && item.Score !== null,
		pointsValue: item.Score,
	});

	return (
		<Card
			ref={lastItemRef}
			className={`relative overflow-hidden border-2 ${
				isGlowPost
					? "border-yellow-500 bg-yellow-50 shadow-lg dark:border-yellow-500 dark:bg-yellow-900/20"
					: postType === "pride"
						? "border-purple-500 bg-purple-50 shadow-lg dark:border-purple-500 dark:bg-purple-900/20"
						: postType === "grateful"
							? "border-green-500 bg-green-50 shadow-lg dark:border-green-500 dark:bg-green-900/20"
							: "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
			}`}
		>
			{/* Instagram-style Header: Avatar and Name at Top - Only show for non-video posts */}
			{item.MediaURL && item.Format === "video" ? null : (
				<CardHeader className="min-w-0 px-3 py-2">
					<div className="flex items-center justify-between">
						<div className="flex min-w-0 flex-1 items-center gap-3">
							<Avatar className="h-10 w-10 flex-shrink-0 border border-border dark:border-border">
								<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
									{item.AuthorName}
								</AvatarFallback>
								<AvatarImage
									src={
										item.AvatarURL ||
										"https://test.goodglows.com/static/goodglows/images/avatars/default.svg"
									}
								/>
							</Avatar>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									<p className="text-sm font-semibold text-foreground dark:text-foreground">
										{item.AuthorName}
									</p>
									{isGlowPost ? (
										<div className="flex items-center gap-1 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-sm">
											<Star size={10} className="text-white" />
											<span>Glow</span>
										</div>
									) : null}
									{postType === "grateful" && (
										<div className="flex items-center gap-1 rounded-full bg-green-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-sm">
											<Heart size={10} className="text-white" />
											<span>Grateful</span>
										</div>
									)}
									{postType === "pride" && (
										<div className="flex items-center gap-1 rounded-full bg-purple-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-sm">
											<Award size={10} className="text-white" />
											<span>Proud</span>
										</div>
									)}
								</div>
								<CardDescription className="text-xs text-muted-foreground dark:text-muted-foreground">
									{new Date(item.CreatedAt).toLocaleDateString()}
								</CardDescription>
							</div>
						</div>
						{/* Temporarily disabled - no menu items available */}
						{/* {isOwnPost && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 rounded-full text-muted-foreground dark:text-muted-foreground hover:bg-muted/50 dark:hover:bg-muted/50"
							>
								<MoreVertical className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							className="bg-popover dark:bg-popover border-border dark:border-border"
						>
							<DropdownMenuItem
								className="text-red-500 focus:text-red-500 hover:bg-muted/50 dark:hover:bg-muted/50"
								onClick={(e) => {
									e.stopPropagation();
									setIsDeleteDialogOpen(true);
								}}
								disabled={isDeleting}
							>
								<Trash2 className="h-4 w-4 mr-2" />
								Delete post
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)} */}
					</div>
				</CardHeader>
			)}

			{/* Instagram-style Media: Full width, prominent placement */}
			<div
				className={`relative w-full overflow-hidden border-y border-border dark:border-border ${isCompact ? "cursor-pointer" : ""}`}
				onClick={() => {
					if (isCompact) {
						onPostSelected(item);
						navigate(`/newsfeed/${postType}/${itemId}`);
					}
				}}
			>
				{item.MediaURL ? (
					item.Format === "video" ? (
						item.MediaURL.includes("youtube.com") ||
						item.MediaURL.includes("youtu.be") ? (
							// Handle YouTube videos with iframe embed
							<div className="relative aspect-video w-full">
								<iframe
									src={getYouTubeEmbedUrl(item.MediaURL, 60)}
									title={item.Title}
									className="h-full w-full"
									frameBorder="0"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
								/>
								{/* Overlay with avatar, username, date, and tag - positioned on top of video */}
								<div className="pointer-events-none absolute left-0 right-0 top-0 z-20 p-3">
									<div className="pointer-events-auto flex max-w-fit items-center gap-3 rounded-lg px-3 py-2">
										<Avatar className="h-10 w-10 flex-shrink-0 border-2 border-white/50">
											<AvatarFallback className="bg-white/20 text-white">
												{item.AuthorName}
											</AvatarFallback>
											<AvatarImage
												src={
													item.AvatarURL ||
													"https://test.goodglows.com/static/goodglows/images/avatars/default.svg"
												}
											/>
										</Avatar>
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<p className="text-sm font-semibold text-white drop-shadow-lg">
													{item.AuthorName}
												</p>
												{isGlowPost ? (
													<div className="flex items-center gap-1 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
														<Star size={10} className="text-white" />
														<span>Glow</span>
													</div>
												) : null}
												{postType === "grateful" && (
													<div className="flex items-center gap-1 rounded-full bg-green-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
														<Heart size={10} className="text-white" />
														<span>Grateful</span>
													</div>
												)}
												{postType === "pride" && (
													<div className="flex items-center gap-1 rounded-full bg-purple-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
														<Award size={10} className="text-white" />
														<span>Proud</span>
													</div>
												)}
											</div>
											<p className="text-xs text-white/90 drop-shadow-md">
												{new Date(item.CreatedAt).toLocaleDateString()}
											</p>
										</div>
									</div>
								</div>
							</div>
						) : (
							// Handle direct video files
							<div className="relative w-full">
								<video
									src={item.MediaURL}
									controls
									autoPlay
									playsInline
									loop
									muted
									className="h-auto max-h-[600px] w-full object-cover"
								/>
								{/* Overlay with avatar, username, date, and tag - positioned on top of video */}
								<div className="pointer-events-none absolute left-0 right-0 top-0 z-20 p-3">
									<div className="pointer-events-auto flex items-center gap-3 rounded-lg px-3 py-2">
										<Avatar className="h-10 w-10 flex-shrink-0 border-2 border-white/50">
											<AvatarFallback className="bg-white/20 text-white">
												{item.AuthorName}
											</AvatarFallback>
											<AvatarImage
												src={
													item.AvatarURL ||
													"https://test.goodglows.com/static/goodglows/images/avatars/default.svg"
												}
											/>
										</Avatar>
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<p className="text-sm font-semibold text-white drop-shadow-lg">
													{item.AuthorName}
												</p>
												{isGlowPost ? (
													<div className="flex items-center gap-1 rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
														<Star size={10} className="text-white" />
														<span>Glow</span>
													</div>
												) : null}
												{postType === "grateful" && (
													<div className="flex items-center gap-1 rounded-full bg-green-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
														<Heart size={10} className="text-white" />
														<span>Grateful</span>
													</div>
												)}
												{postType === "pride" && (
													<div className="flex items-center gap-1 rounded-full bg-purple-500 px-2 py-0.5 text-xs font-semibold uppercase text-white shadow-lg">
														<Award size={10} className="text-white" />
														<span>Proud</span>
													</div>
												)}
											</div>
											<p className="text-xs text-white/90 drop-shadow-md">
												{new Date(item.CreatedAt).toLocaleDateString()}
											</p>
										</div>
									</div>
								</div>
							</div>
						)
					) : item.Format === "audio" ? (
						// Handle audio files with waveform and custom player - full width
						<AudioPlayer
							audioUrl={item.MediaURL}
							onClick={(e) => e.stopPropagation()}
						/>
					) : (
						// Square image format to match crop size (480x480 proportionally)
						<img
							src={item.MediaURL}
							alt={item.Title || "Post image"}
							className="aspect-square w-full object-cover"
						/>
					)
				) : (
					// Placeholder for posts without images - square format (same as crop size proportionally)
					<div className="flex aspect-square w-full items-center justify-center bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-800 dark:to-gray-700">
						<div className="flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
							<Star size={48} className="opacity-50" />
							<span className="text-sm font-medium opacity-75">No Image</span>
						</div>
					</div>
				)}
			</div>

			{/* Instagram-style Content: Description below media (no title) */}
			<CardContent className="min-w-0 px-3 py-2">
				<div
					className={isCompact ? "cursor-pointer" : ""}
					onClick={() => {
						if (isCompact) {
							onPostSelected(item);
							navigate(`/newsfeed/${postType}/${itemId}`);
						}
					}}
				>
					{item.Glow || item.Description ? (
						<p className="break-words text-sm text-muted-foreground dark:text-muted-foreground">
							{item.Glow ? item.Glow : item.Description}
						</p>
					) : null}
					{item.Transcription ? (
						<p className="mt-2 text-xs text-gray-500">
							Transcription: {item.Transcription}
						</p>
					) : null}
				</div>
			</CardContent>
			<CardFooter className="flex w-full min-w-0 flex-col px-3 py-2">
				<div className="flex w-full min-w-0 items-center justify-between gap-2">
					<div className="flex min-w-0 flex-1 gap-2 sm:gap-4">
						<Button
							variant="ghost"
							size="sm"
							className="flex items-center gap-1 px-2 text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
							onClick={(e) => {
								e.stopPropagation();
								if (!userStore.userProgress.isAuthenticated) {
									showErrorToast(
										"Please login or create an account to like a post"
									);
									return;
								}
								newsfeed.handleLike(itemId);
							}}
						>
							<Heart
								className={`h-4 w-4 transition-colors duration-200 ${
									likeIndex !== -1 ? "fill-red-500 text-red-500" : ""
								}`}
							/>
							<span className="text-sm sm:text-base">{item.LikesCount}</span>
						</Button>
						{isCompact && showComments ? (
							<Button
								variant={isCommenting ? "default" : "ghost"}
								size="sm"
								className={`flex items-center gap-1 px-2 ${
									isCommenting
										? "bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700"
										: "text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
								}`}
								onClick={(e) => {
									e.stopPropagation();
									newsfeed.onComment(itemId);
								}}
							>
								<MessageCircle className="h-4 w-4" />
								<span className="text-sm sm:text-base">
									{item.CommentsCount}
								</span>
							</Button>
						) : null}

						<Button
							variant="ghost"
							size="sm"
							className="flex items-center gap-1 px-2 text-muted-foreground hover:bg-muted/50 dark:text-muted-foreground dark:hover:bg-muted/50"
							onClick={(e) => {
								e.stopPropagation();
								if (navigator.share) {
									newsfeed.handleShare(itemId, item.Title);
								} else {
									setShareItemId(itemId);
								}
							}}
						>
							<img
								src={shareArrow}
								alt="Share"
								className="h-4 w-4 opacity-60 dark:opacity-80 dark:invert"
							/>
							<span className="text-sm sm:text-base">{item.SharesCount}</span>
						</Button>

						{/* Send Lumens Button - icon only on mobile, text on larger screens */}
						{onSendLumens ? (
							<Button
								variant="ghost"
								size="sm"
								className="flex items-center gap-1 rounded-lg px-2 py-2 text-gray-600 transition-colors hover:bg-yellow-500/20 hover:text-yellow-600 dark:text-gray-400 sm:px-3"
								onClick={(e) => {
									e.stopPropagation();
									onSendLumens(itemId);
								}}
							>
								<Send className="h-4 w-4" />
								<span className="hidden text-xs sm:inline sm:text-sm">
									Send Lumens
								</span>
							</Button>
						) : null}
					</div>

					{/* Lumens and Points count - bottom right */}
					<div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3">
						{/* Show Points only when viewing a contest feed (not Glow Game) and score exists */}
						{currentContestFilter > 0 &&
							item.Score !== undefined &&
							item.Score !== null && (
								<div className="flex items-center gap-1 text-xs font-bold text-purple-600 dark:text-purple-400 sm:text-sm">
									<span>{item.Score}</span>
									<Trophy className="h-4 w-4" />
								</div>
							)}
						{/* Lumens count */}
						<div className="flex items-center gap-1 text-xs font-bold text-yellow-600 dark:text-yellow-500 sm:text-sm">
							<span>{item.Lumens || 0}</span>
							<Star className="h-4 w-4" />
						</div>
					</div>
				</div>

				{/* Comment section - only shown when commenting on this item and showComments is true */}
				<AnimatePresence mode="wait">
					{showComments && isCommenting ? (
						<CommentsSection
							key={`comments-${itemId}`}
							itemId={itemId}
							isCompact={isCompact}
							isOwnPost={isOwnPost}
							postType={postType}
						/>
					) : null}
				</AnimatePresence>
			</CardFooter>
			<Dialog
				open={shareItemId !== -1}
				onOpenChange={() => {
					if (shareItemId !== -1) {
						setShareItemId(-1);
					}
				}}
			>
				<DialogContent
					className="border-border bg-background dark:border-border dark:bg-background sm:max-w-[425px]"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<DialogHeader>
						<DialogTitle className="text-foreground dark:text-foreground">
							Share Glow
						</DialogTitle>
						<DialogDescription className="text-center text-muted-foreground dark:text-muted-foreground">
							Share a link to this glow's page
						</DialogDescription>
					</DialogHeader>

					<div className="w-full overflow-auto">
						<div className="m-auto flex w-max justify-center gap-3 p-2">
							<Button
								className="h-20 w-20 rounded-full bg-primary p-2 text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
								onClick={() => {
									navigator.clipboard.writeText(
										`${window.location.origin}/newsfeed/${postType}/${itemId}`
									);
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								Copy Link
							</Button>
							<Button
								className="h-20 w-20 rounded-full bg-primary p-2 text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
								onClick={() => {
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								SMS
							</Button>
							<Button
								className="h-20 w-20 rounded-full bg-primary p-2 text-primary-foreground hover:bg-primary/90 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
								onClick={() => {
									newsfeed.handleShare(itemId, item.Title);
									setShareItemId(-1);
								}}
							>
								Email
							</Button>
						</div>
					</div>

					<DialogFooter>
						<DialogClose asChild>
							<Button
								variant="outline"
								className="border-border text-foreground hover:bg-muted/50 dark:border-border dark:text-foreground dark:hover:bg-muted/50"
							>
								Cancel
							</Button>
						</DialogClose>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Temporarily disabled delete functionality */}
			{/* Delete Confirmation Dialog */}
			{/* <AlertDialog
			open={isDeleteDialogOpen}
			onOpenChange={setIsDeleteDialogOpen}
			onInteractOutside={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
		>
			<AlertDialogContent
				onClick={(e) => {
					e.stopPropagation();
				}}
				onInteractOutside={(e) => {
					e.stopPropagation();
					e.preventDefault();
				}}
				className="bg-background dark:bg-background border-border dark:border-border"
			>
				<AlertDialogHeader>
					<AlertDialogTitle className="flex items-center gap-2 text-foreground dark:text-foreground">
						<AlertTriangle className="h-5 w-5 text-red-500" />
						Delete Post
					</AlertDialogTitle>
					<AlertDialogDescription className="text-muted-foreground dark:text-muted-foreground">
						Are you sure you want to delete this post? This action cannot be
						undone.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel
						onClick={(e) => {
							e.stopPropagation();
							setTimeout(() => (document.body.style.pointerEvents = ""), 300);
						}}
						disabled={isDeleting}
						className="border-border dark:border-border text-foreground dark:text-foreground hover:bg-muted/50 dark:hover:bg-muted/50"
					>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={(e) => {
							e.stopPropagation();
							handleDeletePost();
						}}
						disabled={isDeleting}
						className="bg-red-500 hover:bg-red-600 text-white"
					>
						{isDeleting ? "Deleting..." : "Delete"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog> */}
		</Card>
	);
};

export default PostItem;
